import React, { useEffect, useState, useCallback } from 'react';
import { Users, TrendingUp, CheckCircle, PlayCircle, Building2, ChevronRight, UserPlus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { learnersService } from '../services/learners';
import { enrollmentsService } from '../services/enrollments';
import { organizationsService } from '../services/organizations';
import { structuresService } from '../services/structures';
import { LearnerWithProgress, Organization, StructureWithStats } from '../types';
import { PeriodSelector, PeriodType, DateRange, getDateRangeForPeriod } from '../components/Dashboard/PeriodSelector';

interface ClientStats {
  learnersCount: number;
  activeLearnersCount: number;
  completedLearnersCount: number;
  inProgressCount: number;
  completionRate: number;
  averageProgress: number;
  newLearnersCount: number; // Nouveaux apprenants dans la periode
  completedInPeriodCount: number; // Formations terminees dans la periode
}

export const DashboardClient: React.FC = () => {
  const { profile } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [structures, setStructures] = useState<StructureWithStats[]>([]);
  const [learners, setLearners] = useState<LearnerWithProgress[]>([]);
  const [stats, setStats] = useState<ClientStats | null>(null);
  const [selectedStructureId, setSelectedStructureId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('30days');
  const [dateRange, setDateRange] = useState<DateRange>(() => getDateRangeForPeriod('30days'));
  const [periodLabel, setPeriodLabel] = useState<string>('30 derniers jours');

  const handlePeriodChange = useCallback((period: PeriodType, range: DateRange) => {
    setSelectedPeriod(period);
    setDateRange(range);
    const labels: Record<PeriodType, string> = {
      '7days': '7 derniers jours',
      '30days': '30 derniers jours',
      '1year': '1 an',
      'custom': 'Periode selectionnee'
    };
    setPeriodLabel(labels[period]);
  }, []);

  useEffect(() => {
    if (profile?.organizationId) {
      loadDashboardData(dateRange);
    } else {
      setLoading(false);
    }
  }, [profile?.organizationId, dateRange]);

  const loadDashboardData = async (range: DateRange) => {
    if (!profile?.organizationId) return;

    try {
      setLoading(true);

      // Load organization info
      const org = await organizationsService.getById(profile.organizationId);
      setOrganization(org);

      // Load structures with stats
      const structuresData = await structuresService.getWithStats(profile.organizationId);
      setStructures(structuresData);

      // Load learners with progress
      const learnersData = await learnersService.getByOrganization(profile.organizationId);
      setLearners(learnersData);

      // Get enrollment stats
      const enrollmentStats = await enrollmentsService.getOrganizationStats(profile.organizationId);

      // Calculate stats
      const totalLearners = learnersData.length;
      const activeLearners = learnersData.filter(l => l.status === 'active').length;
      const completedLearners = learnersData.filter(l => l.status === 'completed').length;
      const avgProgress = totalLearners > 0
        ? Math.round(learnersData.reduce((sum, l) => sum + l.overallProgress, 0) / totalLearners)
        : 0;

      // Filter by period: new learners created in the period (using timestamps for reliable comparison)
      const startTime = range.startDate.getTime();
      const endTime = range.endDate.getTime();

      const newLearnersInPeriod = learnersData.filter(l => {
        if (!l.createdAt) return false;
        const createdTime = new Date(l.createdAt).getTime();
        return createdTime >= startTime && createdTime <= endTime;
      }).length;

      // Filter by period: count of enrollments completed in the period
      let completedInPeriod = 0;
      learnersData.forEach(l => {
        if (!l.enrollments || l.enrollments.length === 0) return;
        l.enrollments.forEach(e => {
          if (!e.completedAt) return;
          const completedTime = new Date(e.completedAt).getTime();
          if (completedTime >= startTime && completedTime <= endTime) {
            completedInPeriod++;
          }
        });
      });

      setStats({
        learnersCount: totalLearners,
        activeLearnersCount: activeLearners,
        completedLearnersCount: completedLearners,
        inProgressCount: enrollmentStats.inProgressEnrollments,
        completionRate: totalLearners > 0 ? Math.round((completedLearners / totalLearners) * 100) : 0,
        averageProgress: avgProgress,
        newLearnersCount: newLearnersInPeriod,
        completedInPeriodCount: completedInPeriod
      });
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter learners by selected structure
  const filteredLearners = selectedStructureId
    ? learners.filter(l => l.structureId === selectedStructureId)
    : learners;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!profile?.organizationId) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <div className="text-gray-400 dark:text-gray-500 mb-4">
          <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
        </div>
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Aucune organisation associée
        </h2>
        <p className="text-gray-500 dark:text-gray-400">
          Veuillez contacter l'administrateur pour être associé à une organisation.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Tableau de bord
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            {organization?.name || 'Mon organisation'}
          </p>
        </div>
        <PeriodSelector selectedPeriod={selectedPeriod} onPeriodChange={handlePeriodChange} currentRange={dateRange} />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Total apprenants"
          value={stats?.learnersCount || 0}
          color="blue"
        />
        <StatCard
          icon={UserPlus}
          label={`Nouveaux apprenants`}
          value={stats?.newLearnersCount || 0}
          subtitle={periodLabel}
          color="orange"
        />
        <StatCard
          icon={CheckCircle}
          label="Formations terminees"
          value={stats?.completedInPeriodCount || 0}
          subtitle={periodLabel}
          color="green"
        />
        <StatCard
          icon={TrendingUp}
          label="Taux de completion"
          value={`${stats?.completionRate || 0}%`}
          subtitle="global"
          color="purple"
        />
      </div>

      {/* Progress Overview */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Progression moyenne
        </h2>
        <div className="flex items-center gap-4">
          <div className="flex-1 h-4 bg-gray-200 dark:bg-gray-600 rounded-full">
            <div
              className="h-4 bg-primary rounded-full transition-all duration-500"
              style={{ width: `${stats?.averageProgress || 0}%` }}
            />
          </div>
          <span className="text-xl font-bold text-gray-900 dark:text-white">
            {stats?.averageProgress || 0}%
          </span>
        </div>
      </div>

      {/* Structures Section */}
      {structures.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Mes structures
              </h2>
            </div>
            {selectedStructureId && (
              <button
                onClick={() => setSelectedStructureId(null)}
                className="text-sm text-primary hover:text-primary/80"
              >
                Voir toutes les structures
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
            {structures.map((structure) => (
              <div
                key={structure.id}
                onClick={() => setSelectedStructureId(
                  selectedStructureId === structure.id ? null : structure.id
                )}
                className={`p-4 rounded-lg border cursor-pointer transition-all ${
                  selectedStructureId === structure.id
                    ? 'border-primary bg-primary/5 dark:bg-primary/10'
                    : 'border-gray-200 dark:border-gray-600 hover:border-primary/50 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {structure.name}
                    </h3>
                    {structure.externalClientId && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        N° Client: {structure.externalClientId}
                      </p>
                    )}
                  </div>
                  <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${
                    selectedStructureId === structure.id ? 'rotate-90' : ''
                  }`} />
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded p-2">
                    <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                      {structure.learnersCount}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Apprenants</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded p-2">
                    <p className="text-lg font-bold text-green-600 dark:text-green-400">
                      {structure.completedCount}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Terminés</p>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded p-2">
                    <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
                      {structure.completionRate}%
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Complétion</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Learners Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {selectedStructureId
              ? `Apprenants - ${structures.find(s => s.id === selectedStructureId)?.name || ''}`
              : 'Mes apprenants'}
          </h2>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {filteredLearners.length} apprenant{filteredLearners.length > 1 ? 's' : ''}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Apprenant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Département
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Progression
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredLearners.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    {selectedStructureId
                      ? 'Aucun apprenant dans cette structure'
                      : 'Aucun apprenant pour le moment'}
                  </td>
                </tr>
              ) : (
                filteredLearners.slice(0, 10).map((learner) => (
                  <tr key={learner.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {learner.fullname}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {learner.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                      {learner.department || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <LearnerStatusBadge status={learner.status} />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-600 rounded-full max-w-[100px]">
                          <div
                            className="h-2 bg-primary rounded-full"
                            style={{ width: `${learner.overallProgress}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-600 dark:text-gray-300 w-10">
                          {learner.overallProgress}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filteredLearners.length > 10 && (
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 text-center">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Et {filteredLearners.length - 10} autres apprenants...
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Stat Card Component
interface StatCardProps {
  icon: React.FC<{ className?: string }>;
  label: string;
  value: string | number;
  color: 'blue' | 'green' | 'orange' | 'purple';
  subtitle?: string;
}

const colorClasses = {
  blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  green: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
  orange: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
  purple: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
};

const StatCard: React.FC<StatCardProps> = ({ icon: Icon, label, value, color, subtitle }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
    <div className="flex items-center gap-4">
      <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
        <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
        {subtitle && (
          <p className="text-xs text-gray-400 dark:text-gray-500">{subtitle}</p>
        )}
      </div>
    </div>
  </div>
);

// Learner Status Badge
const LearnerStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles = {
    active: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    inactive: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
  };

  const labels = {
    active: 'En formation',
    completed: 'Terminé',
    inactive: 'Inactif'
  };

  return (
    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${styles[status as keyof typeof styles] || styles.inactive}`}>
      {labels[status as keyof typeof labels] || status}
    </span>
  );
};
